import { useCallback } from 'react'
import { trpc } from '../../../../lib/trpc.js'
import { useResource } from './use-resource'

export interface LinkedRecipe {
  id: string
  name: string
  slug: string
}

export interface Subtopic {
  id: string
  slug: string
  name: string
  description?: string | null
  topicId: string
  topicName: string
  displayOrder: number
  recipes: LinkedRecipe[]
  createdAt: string
  updatedAt: string
}

export interface Topic {
  id: string
  name: string
  slug: string
  displayOrder: number
}

export interface SubtopicFormData {
  topicId: string
  name: string
  description?: string
  recipeIds?: string[]
}

const selectSubtopics = (data: Subtopic[] | undefined): Subtopic[] => data ?? []

export function useSubtopics() {
  const utils = trpc.useUtils()

  const invalidate = useCallback(() => {
    utils.admin.subtopics.list.invalidate()
  }, [utils])

  const {
    items: subtopics,
    loading: subtopicsLoading,
    error: subtopicsError,
    create,
    update,
    remove,
  } = useResource({
    query: trpc.admin.subtopics.list.useQuery(),
    select: selectSubtopics,
    invalidate,
    create: trpc.admin.subtopics.create,
    update: trpc.admin.subtopics.update,
    remove: trpc.admin.subtopics.delete,
  })

  // Topics are a separate read-only lookup that shares this hook's loading and
  // error surface.
  const {
    data: topicsData,
    isLoading: topicsLoading,
    error: topicsError,
  } = trpc.admin.subtopics.listTopics.useQuery()

  const topics: Topic[] = topicsData ?? []
  const loading = subtopicsLoading || topicsLoading
  const error = subtopicsError ?? topicsError?.message ?? null

  // Note: create/update results omit topicName (callers refetch via the
  // list invalidation above), so the inferred mutation output type is used.
  const createSubtopic = async (formData: SubtopicFormData) => {
    return create(formData)
  }

  const updateSubtopic = async (
    id: string,
    formData: Partial<Omit<SubtopicFormData, 'topicId'>>,
  ) => {
    return update({ id, ...formData })
  }

  const deleteSubtopic = async (id: string): Promise<void> => {
    await remove({ id })
  }

  const refresh = () => {
    utils.admin.subtopics.list.invalidate()
    utils.admin.subtopics.listTopics.invalidate()
  }

  return {
    subtopics,
    topics,
    loading,
    error,
    refresh,
    createSubtopic,
    updateSubtopic,
    deleteSubtopic,
  }
}
